import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Brand } from "@/types/brand";
import { CheckCircle2 } from "lucide-react";

interface CompletionStepProps {
  data: Brand;
  onComplete: () => void;
  isSubmitting: boolean;
}

export function CompletionStep({
  data,
  onComplete,
  isSubmitting,
}: CompletionStepProps) {
  // Using the brand name from the data
  const name = data.name || "your brand";
  return (
    <Card className="w-full max-w-3xl mx-auto">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <CheckCircle2 className="h-10 w-10 text-primary" />
          </div>
        </div>
        <CardTitle className="text-2xl">You're Almost There, {name}!</CardTitle>
        <CardDescription className="text-base">
          Let's help you create your first article and start driving results
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="bg-muted p-6 rounded-lg">
          <h3 className="font-medium text-lg mb-4">
            Here's what you can do next:
          </h3>
          <ul className="space-y-4">
            <li className="flex items-start">
              <div className="bg-primary/10 p-1 rounded-full mr-3 mt-0.5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-primary"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <strong>Generate AI Content</strong>
                <p className="text-sm text-muted-foreground">
                  Create SEO-optimized articles tailored to your brand voice
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <div className="bg-primary/10 p-1 rounded-full mr-3 mt-0.5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-primary"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <strong>Analyze Your SEO Performance</strong>
                <p className="text-sm text-muted-foreground">
                  Get insights and recommendations to improve your rankings
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <div className="bg-primary/10 p-1 rounded-full mr-3 mt-0.5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-primary"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <strong>Plan Your Content Calendar</strong>
                <p className="text-sm text-muted-foreground">
                  Schedule and organize your content strategy
                </p>
              </div>
            </li>
          </ul>
        </div>

        <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6 rounded-lg text-center">
          <h3 className="font-medium text-lg mb-2">
            Ready to Create Your First Article?
          </h3>
          {/*<p className="text-muted-foreground mb-4">
            Start with a free article to experience our AI-powered content creation.
            No subscription required for your first piece!
          </p>*/}
          <Button
            onClick={onComplete}
            className="w-full"
            size="lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Setting Up Your Content Studio...
              </>
            ) : (
              "Create My First Article"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
